import { z } from 'zod';

export const TITLE_MAX_CHARACTERS = 255;
export const DESCRIPTION_MAX_CHARACTERS = 5000;
export type FollowUpActionMode = 'add' | 'edit';

export type ReplyTypes = ['task', 'form'];
export const AllReplyTypes: ReplyTypes = ['task', 'form'];
export type ReplyType = ReplyTypes[number];

const FollowUpActionImageSchema = z.object({
    is_low_quality: z.boolean().optional().nullable(),
    laplacian_variance: z.number().optional().nullable(),
    s3_key: z.string().nullable()
});

export const FollowUpActionFileSchema = FollowUpActionImageSchema.extend({
    name: z.string(),
    raw_file: z.string(),
    size: z.number().optional(),
    type: z.string()
});

export const FollowUpActionResponseDataSchema = z.object({
    assigned_user: z
        .object({
            id: z.union([z.number(), z.string()]),
            display_name: z.string()
        })
        .nullable(),
    completed_user: z
        .object({
            id: z.union([z.number(), z.string()]),
            display_name: z.string()
        })
        .nullable(),
    created_user: z.object({
        id: z.union([z.number(), z.string()]),
        display_name: z.string()
    }),
    data: z.object({
        description: z.string().optional().nullable(),
        form: z
            .object({
                id: z.union([z.number(), z.string()]),
                title: z.string()
            })
            .optional()
            .nullable(),
        images: z.array(z.string()).optional().nullable(),
        metadata: z.record(z.string(), z.any()).optional().nullable(),
        reply: z.record(z.string(), z.any()).optional().nullable()
    }),
    date_assigned: z.string().nullable(),
    date_completed: z.string().nullable(),
    date_created: z.string(),
    date_deleted: z.string().nullable(),
    date_due: z.union([z.string(), z.date()]),
    deleted_user: z
        .object({
            id: z.union([z.number(), z.string()]),
            display_name: z.string()
        })
        .nullable(),
    modified_user: z.object({
        id: z.union([z.number(), z.string()]),
        display_name: z.string()
    }),
    has_delete_permission: z.boolean(),
    has_update_permission: z.boolean(),
    id: z.number(),
    location: z.object({ id: z.number(), name: z.string(), time_zone: z.string() }),
    role: z
        .object({
            id: z.union([z.number(), z.string()]),
            name: z.string()
        })
        .nullable(),
    source_id: z.string().nullable(),
    source_title: z.string().nullable(),
    source_type: z.string(),
    reply_type: z.string(),
    status: z.string(),
    team: z
        .object({
            id: z.union([z.number(), z.string()]),
            name: z.string()
        })
        .nullable()
        .nullish(),
    title: z.string().min(1).max(TITLE_MAX_CHARACTERS)
});

export const FollowUpActionReplySchema = z.object({
    images: z.array(FollowUpActionImageSchema),
    note: z.string().min(0).max(DESCRIPTION_MAX_CHARACTERS).optional(),
    status: z.string()
});

export const IncompleteFollowUpActionSchema = z.object({
    assigned_user: z
        .object({
            id: z.union([z.number(), z.string()]),
            display_name: z.string()
        })
        .nullable()
        .optional(),
    description: z.string().min(1).max(DESCRIPTION_MAX_CHARACTERS),
    due_date: z.date(),
    files: z.array(FollowUpActionFileSchema).optional(),
    form: z
        .object({
            id: z.union([z.number(), z.string()]),
            title: z.string()
        })
        .optional()
        .nullable(),
    reply_type: z.enum(AllReplyTypes).nullable().optional(),
    require_photo: z.boolean(),
    title: z.string().min(1).max(TITLE_MAX_CHARACTERS)
});

// Before saving a follow up action in the database. Creation process
export const InitialFollowUpActionSchema = z.object({
    assigned_user: z
        .object({
            id: z.union([z.number(), z.string()]),
            display_name: z.string()
        })
        .nullable()
        .optional(),
    description: z.string().min(1).max(DESCRIPTION_MAX_CHARACTERS),
    due_date: z.date(),
    form: z.object({ id: z.number(), label: z.string() }).nullable().optional(),
    images: z.array(FollowUpActionFileSchema),
    location: z.object({ id: z.number(), label: z.string(), time_zone: z.string() }),
    reply_type: z.enum(AllReplyTypes),
    require_photo: z.boolean(),
    title: z.string().min(1).max(TITLE_MAX_CHARACTERS)
});

export type FollowUpActionType = z.infer<typeof FollowUpActionResponseDataSchema>;
export type FollowUpActionImageType = z.infer<typeof FollowUpActionImageSchema>;
export type FollowUpActionFileType = z.infer<typeof FollowUpActionFileSchema>;
export type IncompleteFollowUpActionType = z.infer<typeof IncompleteFollowUpActionSchema>;
export type InitialFollowUpActionType = z.infer<typeof InitialFollowUpActionSchema>;
export type ReplyFollowUpActionType = z.infer<typeof FollowUpActionReplySchema>;
// When updating or deleting follow up actions before saving them to the database
export type TemporalFollowUpActionType = z.infer<typeof InitialFollowUpActionSchema> & {
    temp_id?: string;
};

//Comes from ZenputFile model
export type ZenputFileType = {
    id: number;
    raw_file: File;
    mimetype: string;
    metadata: string;
    url_base64: string;
    url_native: string;
    url_cdva: string;
    url_s3: string;
    original_url_native: string;
    legacy_file_structure: string;
    size: number;
    path: string;
    laplacian_variance: number;
    is_low_quality: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
};

export type ZenputFileModel = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get: (key: string) => any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    set: (key: string, value: any) => void;
} & ZenputFileType;

export type ZenputFileCollection = {
    models: ZenputFileModel[];
    length: number;
};

export type WebMediaResponseType = {
    files: ZenputFileCollection;
    displayUrls: FollowUpActionFileType[];
};

export interface TaskItemContext {
    deleteFollowUpAction: (id: number, onError: () => void) => void;
    getS3URL: (value: string) => string;
    hasDetectLowQualityPhotosDF: boolean;
    onCompleteFollowUpAction: (message: string) => void;
    updateFollowUpAction: (
        followUpAction: FollowUpActionType,
        onComplete: (response: FollowUpActionType) => void,
        onError: () => void
    ) => void;
    uploadFilesToS3: (files: ZenputFileCollection) => Promise<(string | null)[]>;
}

export interface MobileFormContext {
    allowAccessToReportingLocations: boolean;
    formReplyEnabled: boolean;
    getS3URL: (value: string) => string;
    uploadFilesToS3: (files: ZenputFileCollection) => Promise<(string | null)[]>;
}
